import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, PlusIcon, MinusIcon, TrashIcon, CheckCircleIcon, UserIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useProducts } from '../hooks/useProducts';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useMyOrders, useCreateOrder, useSubmitOrder, useStorerooms, useReorderSuggestions, useCancelOrder, useConsumableCatalogue } from '../hooks/useStockOrders';

const STATUS = {
  draft: 'bg-gray-100 text-gray-700', submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800', cancelled: 'bg-gray-100 text-gray-500',
};

// Item-type codes are the prefix of the SKU. Friendly labels are best-guess
// from the SKU scheme — correct them here (or in the SKU manager) if wrong.
const CATEGORY_LABELS = {
  CI: 'Cable & Install', CC: 'Civil / Groundworks', HT: 'Hand Tools', HTC: 'Hand Tool Consumables',
  U: 'Uniform', HS: 'Health & Safety', HSC: 'H&S Consumables', PPE: 'PPE', OFF: 'Office',
  PT: 'Power Tools', PTC: 'Power Tool Consumables', AST: 'IT & Assets', V: 'Vehicle',
  FT: 'Fibre Tools', CF: 'CF', CPC: 'CPC', Uncategorised: 'Uncategorised',
};
// The dropdown is built by mapping this array, so a category missing from it
// can never be filtered on however many products carry it. 'ISP Stock' is the
// client-supplied ISP range, fed from the ISP SKU Manager by
// services/ispSkuProductsSync.js in tech -- first because it is the range Ops
// actually works from, and it is not a SKU-prefix type code like the rest.
const CATEGORY_ORDER = ['ISP Stock', 'CI', 'CC', 'FT', 'PT', 'PTC', 'HT', 'HTC', 'HS', 'HSC', 'PPE', 'U', 'V', 'AST', 'OFF', 'CF', 'CPC', 'Uncategorised'];
const catLabel = (c) => CATEGORY_LABELS[c] || c || 'Uncategorised';

function Stepper({ value, onChange }) {
  const set = (v) => onChange(Math.max(1, v));
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={() => set(value - 1)} className="flex h-7 w-7 items-center justify-center rounded border border-gray-300"><MinusIcon className="h-3.5 w-3.5" /></button>
      <input type="number" min={1} value={value} onChange={(e) => set(parseInt(e.target.value, 10) || 1)} className="h-7 w-12 rounded border border-gray-300 text-center text-sm" />
      <button type="button" onClick={() => set(value + 1)} className="flex h-7 w-7 items-center justify-center rounded border border-gray-300"><PlusIcon className="h-3.5 w-3.5" /></button>
    </span>
  );
}

function StockProcurement() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [storeroom, setStoreroom] = useState('');
  const [notes, setNotes] = useState('');
  const [flash, setFlash] = useState(null);

  const { data: me } = useCurrentUser();
  const { data: storerooms } = useStorerooms();
  const { data: search } = useProducts({ q, category, active: '1', limit: 300 });
  const { data: consumables } = useConsumableCatalogue(q);
  const { data: myOrders } = useMyOrders();
  const createOrder = useCreateOrder();
  const submitOrder = useSubmitOrder();
  const cancelOrder = useCancelOrder();
  const { data: reorder } = useReorderSuggestions();
  const ispCodes = useMemo(() => new Set((reorder && reorder.isp_item_codes) || []), [reorder]);
  const ispSuggestions = (reorder && reorder.suggestions) || [];

  const addToCart = (p) => setCart((c) => c.find((x) => x.key === p.key)
    ? c.map((x) => x.key === p.key ? { ...x, quantity: x.quantity + 1 } : x)
    : [...c, {
      key: p.key,
      product_id: p.storeroom_item_id ? null : p.id,
      storeroom_item_id: p.storeroom_item_id || null,
      item_code: p.item_code, name: p.name, quantity: 1,
    }]);
  const setQty = (key, qty) => setCart((c) => c.map((x) => x.key === key ? { ...x, quantity: qty } : x));
  const remove = (key) => setCart((c) => c.filter((x) => x.key !== key));

  // Group the catalog results by item-type category for browsing.
  const grouped = useMemo(() => {
    const products = (search?.items || [])
      .filter((it) => !ispCodes.has(it.item_code))
      .map((it) => ({ ...it, key: `p-${it.id}` }));
    // Storeroom consumables sit alongside the purchasing catalogue rather than
    // in a separate tab: a storeman thinks in items, not in which table an
    // item happens to live in.
    const cons = (consumables || []).map((it) => ({
      ...it, key: `c-${it.storeroom_item_id}`,
    }));
    const items = [...products, ...cons];
    const m = new Map();
    for (const it of items) { const k = it.category || 'Uncategorised'; if (!m.has(k)) m.set(k, []); m.get(k).push(it); }
    return Array.from(m.entries()).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a[0]); const ib = CATEGORY_ORDER.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [search, consumables, ispCodes]);

  const submit = async () => {
    if ((!cart.length && !ispSuggestions.length) || !storeroom) return;
    const body = { delivery_location: storeroom, notes: notes || null, lines: [...ispSuggestions.map((x) => ({ product_id: x.product_id, quantity: x.used })), ...cart.map((x) => (x.storeroom_item_id
      ? { storeroom_item_id: x.storeroom_item_id, quantity: x.quantity }
      : { product_id: x.product_id, quantity: x.quantity }))] };
    const created = await createOrder.mutateAsync(body);
    const detail = await submitOrder.mutateAsync(created.order.id);
    setFlash(`Order ${detail.order.reference} submitted for approval.`);
    setCart([]); setNotes('');
  };
  const busy = createOrder.isPending || submitOrder.isPending;

  return (
    <div className="p-6">
      <Link to="/?dept=logistics" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" />
        Back to Logistics
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">Stock Procurement</h1>
      <p className="mt-1 text-sm text-gray-500">Raise a stock order from the master catalog. It goes to the Head of Logistics for final approval, who assigns suppliers and generates the purchase orders.</p>

      {/* Requested by (auto) */}
      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
        <UserIcon className="h-4 w-4 text-gray-400" /> Ordered by <span className="font-semibold">{me?.display_name || '…'}</span>
      </div>

      {flash && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          <CheckCircleIcon className="h-5 w-5" /> {flash}
        </div>
      )}

      <div className="mt-5 grid gap-6 xl:grid-cols-[1fr_26rem]">
        {/* Catalog picker */}
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-400">Add consumables</h2>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">All types</option>
              {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
            </select>
            <div className="relative ml-auto min-w-[16rem] flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search consumables…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
            </div>
          </div>
          <div className="max-h-[36rem] space-y-4 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3">
            {grouped.map(([cat, items]) => (
              <div key={cat}>
                <p className="sticky top-0 bg-white pb-1 text-xs font-bold uppercase tracking-wide text-brand">{catLabel(cat)} <span className="text-gray-300">· {items.length}</span></p>
                <div className="divide-y divide-gray-100">
                  {items.map((p) => (
                    <button key={p.key} type="button" onClick={() => addToCart(p)} className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-brand-light/40">
                      <span className="min-w-0 truncate text-sm text-gray-900">{p.name}</span>
                      <PlusIcon className="h-5 w-5 shrink-0 text-brand" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {grouped.length === 0 && <p className="py-8 text-center text-sm text-gray-400">No items match.</p>}
          </div>
        </div>

        {/* Cart / order */}
        <div>
          {ispSuggestions.length > 0 && (
            <div className="mb-3">
              <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-400">ISP stock — auto from job cards</h2>
              <div className="rounded-lg border border-sky-200 bg-sky-50/50">
                {ispSuggestions.map((x) => (
                  <div key={x.product_id} className="flex items-center justify-between gap-3 border-b border-sky-100 px-3 py-2 last:border-0">
                    <span className="min-w-0 truncate text-sm text-gray-900">{x.name}</span>
                    <span className="text-sm font-semibold tabular-nums text-sky-800">{x.used}</span>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-400">Calculated from ISP stock you used since your last order — not editable.</p>
            </div>
          )}
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-400">Consumables ({cart.length})</h2>
          <div className="rounded-lg border border-gray-200 bg-white">
            {cart.length === 0 && <p className="px-3 py-8 text-center text-sm text-gray-400">Search and add items from the catalog.</p>}
            {cart.map((x) => (
              <div key={x.key} className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-0">
                <span className="min-w-0 truncate text-sm text-gray-900">{x.name}</span>
                <span className="flex items-center gap-2"><Stepper value={x.quantity} onChange={(v) => setQty(x.key, v)} /><button type="button" onClick={() => remove(x.key)} className="text-gray-300 hover:text-rose-500"><TrashIcon className="h-4 w-4" /></button></span>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">Deliver to storeroom <span className="text-rose-500">*</span></span>
              <select value={storeroom} onChange={(e) => setStoreroom(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select a storeroom…</option>
                {(storerooms || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            <button type="button" onClick={submit} disabled={busy || (!cart.length && !ispSuggestions.length) || !storeroom} className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
              {busy ? 'Submitting…' : 'Submit order for approval'}
            </button>
            {!storeroom && cart.length > 0 && <p className="text-center text-xs text-gray-400">Pick a storeroom to submit.</p>}
          </div>
        </div>
      </div>

      {/* My orders */}
      <h2 className="mb-2 mt-8 text-sm font-bold uppercase tracking-wide text-gray-400">My orders</h2>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr><th className="px-3 py-2">Reference</th><th className="px-3 py-2">Items</th><th className="px-3 py-2">Storeroom</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Raised</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(myOrders || []).map((o) => (
              <tr key={o.id}>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{o.reference || `#${o.id} (draft)`}</td>
                <td className="px-3 py-2 text-gray-700">{o.line_count}</td>
                <td className="px-3 py-2 text-gray-600">{o.delivery_location || '—'}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[o.status] || 'bg-gray-100'}`}>{o.status}</span>{o.status === 'rejected' && o.rejected_reason ? <span className="ml-2 text-xs text-rose-500">{o.rejected_reason}</span> : null}</td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleDateString('en-GB') : ''}</td>
                <td className="px-3 py-2 text-right">
                  {o.can_cancel && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm('Cancel order ' + (o.reference || '#' + o.id) + '? This cannot be undone.')) return;
                        try { await cancelOrder.mutateAsync(o.id); setFlash('Order cancelled.'); }
                        catch (e) { window.alert(e?.response?.data?.error || 'Could not cancel order.'); }
                      }}
                      className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {myOrders && myOrders.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default StockProcurement;
